import { useCallback, useRef } from 'react';

import {
  trackSpotlightContactFormAbandoned,
  trackSpotlightContactFormStarted,
  trackSpotlightContactFormSubmitted,
} from '@/lib/mixpanel/mixpanel';

export type SpotlightContactFormCloseTracking = {
  trackAbandoned: () => void;
};

interface UseSpotlightContactFormTrackingParams {
  companyId: string;
  companyName: string;
  userId: number | null;
}

export function useSpotlightContactFormTracking({
  companyId,
  companyName,
  userId,
}: UseSpotlightContactFormTrackingParams) {
  const hasStartedRef = useRef(false);
  const hasSubmittedRef = useRef(false);

  const handleFieldInteraction = useCallback(() => {
    if (hasStartedRef.current || userId === null) {
      return;
    }

    hasStartedRef.current = true;
    trackSpotlightContactFormStarted({
      company_id: companyId,
      company_name: companyName,
      user_id: userId,
    });
  }, [companyId, companyName, userId]);

  const trackFormSubmitted = useCallback(() => {
    if (userId === null) {
      return;
    }

    hasSubmittedRef.current = true;
    trackSpotlightContactFormSubmitted({
      company_id: companyId,
      company_name: companyName,
      user_id: userId,
    });
  }, [companyId, companyName, userId]);

  const trackFormAbandoned = useCallback(() => {
    if (!hasStartedRef.current || hasSubmittedRef.current || userId === null) {
      return;
    }

    trackSpotlightContactFormAbandoned({
      company_id: companyId,
      company_name: companyName,
      user_id: userId,
    });
  }, [companyId, companyName, userId]);

  return {
    handleFieldInteraction,
    trackFormAbandoned,
    trackFormSubmitted,
  };
}
